import { Eye, Plus, Trash2 } from 'lucide-react';
import { alphaChannels, captureAlphaChannel, SetAlphaChannelsCommand } from '../core/channels';
import { SelectionCommand } from '../core/commands';
import type { EditorDocument } from '../core/model';
import { hasSelection } from '../core/selection';
import type { DocumentSession } from '../core/session';
import { runCommand } from '../semantic/commands/run';

export type ChannelView = 'composite' | 'red' | 'green' | 'blue' | `alpha:${string}`;

export function ChannelsPanel({ document, session, view, onView }: {
  document: EditorDocument;
  session: DocumentSession;
  view: ChannelView;
  onView: (view: ChannelView) => void;
}) {
  const history = session.history;
  const channels = alphaChannels(document);
  const rename = (id: string, current: string) => {
    const name = window.prompt('Alpha channel name', current)?.trim();
    if (!name || name === current) return;
    runCommand(session, 'channels.rename', { channel: id, name });
  };
  return <div className="channels-panel">
    <div className="channels-list">
      {([['composite', 'RGB composite'], ['red', 'Red'], ['green', 'Green'], ['blue', 'Blue']] as const).map(([id, label]) => <button type="button" className={view === id ? 'active' : ''} key={id} onClick={() => onView(id)}><Eye size={13} /><span>{label}</span></button>)}
      {channels.map((channel) => <div className={`alpha-channel ${view === `alpha:${channel.id}` ? 'active' : ''}`} key={channel.id}>
        <button type="button" onDoubleClick={() => rename(channel.id, channel.name)} onClick={() => onView(`alpha:${channel.id}`)}><Eye size={13} /><span>{channel.name}<small>{channel.selection.inverted ? 'Inverted' : `${channel.selection.regions.length} region${channel.selection.regions.length === 1 ? '' : 's'}`}</small></span></button>
        <button type="button" title={`Load ${channel.name} as selection`} onClick={() => history.execute(document, new SelectionCommand(`Load ${channel.name}`, () => structuredClone(channel.selection)))}>Load</button>
        <button type="button" aria-label={`Delete ${channel.name}`} onClick={() => runCommand(session, 'channels.delete', { channel: channel.id })}><Trash2 size={11} /></button>
      </div>)}
    </div>
    <div className="channels-footer"><button type="button" disabled={!hasSelection(document.selection) || channels.length >= 8} onClick={() => {
      const channel = captureAlphaChannel(document);
      history.execute(document, new SetAlphaChannelsCommand([...channels, channel], 'Save selection as alpha channel'));
      onView(`alpha:${channel.id}`);
    }}><Plus size={12} /> Save selection</button><small>{channels.length}/8 alpha channels</small></div>
  </div>;
}
